// =====================================================================================
// useDecorationPalette - Decoration palette loading and category management
// =====================================================================================
// Loads available decorations from tileset data, manages category filtering,
// and provides the palette state for the decoration editor UI.

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useGameInstance } from '../contexts/GameInstanceContext.js';
import { DecorationPaletteLoader } from '../game/utils/DecorationPaletteLoader.js';
import type { DecorationItem } from '../contexts/editMode.types.js';
import type { TilemapTileset } from '../game/utils/TilesetResolver.js';

/**
 * Hook that manages the decoration palette: loading decorations from tilesets,
 * organizing them by category, and providing filtered results.
 */
export function useDecorationPalette(): {
  availableDecorations: DecorationItem[];
  categories: string[];
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  loadDecorationsFromTilesets: (tilesets: TilemapTileset[]) => Promise<void>;
  filteredDecorations: DecorationItem[];
  setError: (error: string | null) => void;
} {
  const { getGameScene } = useGameInstance();

  // Available decorations loaded from tilemap
  const [availableDecorations, setAvailableDecorations] = useState<DecorationItem[]>([]);

  // Categories derived from available decorations
  const [categories, setCategories] = useState<string[]>(['all']);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Error setter (passed from parent for centralized error state)
  const [localError, setLocalError] = useState<string | null>(null);

  // Track if decorations have been loaded to prevent duplicate loading
  const decorationsLoadedRef = useRef(false);

  // Update categories when decorations change
  useEffect(() => {
    const uniqueCategories = new Set<string>();
    uniqueCategories.add('all');
    availableDecorations.forEach(d => uniqueCategories.add(d.category));
    setCategories(Array.from(uniqueCategories).sort());
  }, [availableDecorations]);

  // Load decorations from tileset data
  const loadDecorationsFromTilesets = useCallback(async (tilesets: TilemapTileset[]) => {
    const scene = getGameScene();
    if (!scene) {
      return;
    }

    // Prevent duplicate loading
    if (decorationsLoadedRef.current) {
      return;
    }

    try {
      decorationsLoadedRef.current = true;

      // First, preload textures needed for palette items
      const decorationSystem = (scene as any).systems?.decorations;

      if (decorationSystem) {
        try {
          await decorationSystem.preloadPaletteTextures(tilesets);
        } catch (err) {
          console.warn('[EditMode] Failed to preload palette textures:', err);
        }
      }

      // Create a DecorationPaletteLoader instance
      const loader = new DecorationPaletteLoader(scene);

      // Load decorations from the tilesets
      const decorations = loader.loadDecorationsFromTilesets(tilesets);

      // Load preview images for decorations
      const decorationsWithPreviews = await loader.loadAllPreviews(decorations);

      // Update state with loaded decorations
      setAvailableDecorations(decorationsWithPreviews);

    } catch (err) {
      decorationsLoadedRef.current = false; // Reset on error to allow retry
      setLocalError('Failed to load decoration palette');
    }
  }, [getGameScene]);

  // Filter decorations by selected category
  const filteredDecorations = useMemo(() =>
    selectedCategory === 'all'
      ? availableDecorations
      : availableDecorations.filter(d => d.category === selectedCategory),
    [availableDecorations, selectedCategory]
  );

  return {
    availableDecorations,
    categories,
    selectedCategory,
    setSelectedCategory,
    loadDecorationsFromTilesets,
    filteredDecorations,
    setError: setLocalError
  };
}
